/* eslint-disable no-undef */
import { Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { useGetExtendedResponseTapeDownloadUrlLazyQueryGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Status } from './AssetDetailsSummary';
import { colors } from 'packages/common-frontend/colors';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import {
  errorToastId,
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';

const TooltipWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: React.ReactNode) => JSX.Element;
  children: JSX.Element;
}) => (condition ? wrapper(children) : children);

type EligibilityTestRow = {
  eligibilityTestName: string;
  eligibilityTestFormula?: string;
  eligibilityResultsByReportingDates: { [date: string]: string };
};

type EligibilityTestsTableProps = {
  historicalRecords: any[];
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  downloadFile: (reportingDate: Util.FullDateStr, fileVersion: number) => void;
  loanTapeDownloadReportingDate?: string;
  setLoanTapeDownloadReportingDate: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
};
const EligibilityTestsTable = ({
  historicalRecords,
  loanProductMechanic,
  downloadFile,
  loanTapeDownloadReportingDate,
  setLoanTapeDownloadReportingDate,
}: EligibilityTestsTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const eligibilityData: EligibilityTestRow[] = useMemo(() => {
    const eligibilityTestRowMap = new Map<string, EligibilityTestRow>();
    loanProductMechanic.eligibilityTests.forEach((eligibilityTest) => {
      if (eligibilityTest._config_.eligibilityTestConfig) {
        eligibilityTestRowMap.set(
          eligibilityTest._config_.eligibilityTestConfig.propertyKey.toLowerCase(),
          {
            eligibilityTestName: eligibilityTest.name,
            eligibilityTestFormula:
              eligibilityTest._config_.eligibilityTestConfig.type === 'formula'
                ? eligibilityTest._config_.eligibilityTestConfig.config.formula
                : undefined,
            eligibilityResultsByReportingDates: {},
          },
        );
      }
    });
    historicalRecords.forEach((historicalRecord) => {
      [...eligibilityTestRowMap.keys()].forEach((key) => {
        const reportingDate = historicalRecord.reportingdate;
        const eligibleStatus = historicalRecord[`ess.${key}.es`];
        const eligibilityTestRow = {
          ...(eligibilityTestRowMap.get(key) ?? {
            eligibilityTestName: key,
            eligibilityResultsByReportingDates: {},
          }),
        };

        eligibilityTestRow.eligibilityResultsByReportingDates[
          reportingDate
        ] = eligibleStatus;
        eligibilityTestRowMap.set(key, eligibilityTestRow);
      });
    });
    return [...eligibilityTestRowMap.values()];
  }, [historicalRecords, loanProductMechanic]);

  const reportingDateColumns: ColumnDef<any, any>[] = useMemo(() => {
    return historicalRecords.map((historicalRecord) => {
      const reportingDate = historicalRecord.reportingdate;
      const fileVersion = historicalRecord.fileversion;
      return {
        id: reportingDate,
        accessorFn: (row) =>
          row.eligibilityResultsByReportingDates[reportingDate],
        /* eslint-disable react/display-name */
        header: () => {
          return (
            <Flex alignItems="center">
              <Text color="black" fontSize="16px" mr="8px">
                {commonDate.fullDateStrToShortMonthDayString(reportingDate)}
              </Text>
              <DownloadIconButton
                ariaLabel="download extended response tape"
                label="Download extended response tape"
                isLoading={loanTapeDownloadReportingDate === reportingDate}
                isDisabled={
                  loanTapeDownloadReportingDate !== undefined &&
                  loanTapeDownloadReportingDate !== reportingDate
                }
                fontSize="xl"
                color={colors.info}
                onClick={() => {
                  setLoanTapeDownloadReportingDate(reportingDate);
                  downloadFile(reportingDate, fileVersion);
                }}
              />
            </Flex>
          );
        },
        cell: ({ getValue }: { getValue: any }) => {
          if (getValue() === undefined) {
            return <></>;
          }
          const isEligible = getValue() === 'ELIGIBLE';
          return (
            <Status
              icon={
                <Icon
                  icon={isEligible ? 'circle-check' : 'circle-xmark'}
                  color={isEligible ? colors.green[400] : colors.red[400]}
                  size="large"
                />
              }
              label={isEligible ? 'Eligible' : 'Not Eligible'}
              theme={isEligible ? 'success' : 'error'}
              width="150px"
            />
          );
        },
        meta: {
          style: { padding: '16px' },
        },
        size: 200,
      };
    });
  }, [
    downloadFile,
    historicalRecords,
    loanTapeDownloadReportingDate,
    setLoanTapeDownloadReportingDate,
  ]);

  const columns: ColumnDef<any, any>[] = [
    {
      id: 'eligibilityTestName',
      accessorKey: 'eligibilityTestName',
      header: () => {
        return (
          <Text color="black" fontSize="16px">
            Eligibility Test
          </Text>
        );
      },
      cell: ({ row, getValue }) => {
        return (
          <TooltipWrapper
            condition={row.original.eligibilityTestFormula !== undefined}
            wrapper={(children) => (
              <Tooltip
                label={
                  <Text as="pre">{row.original.eligibilityTestFormula}</Text>
                }
                maxWidth="min-content"
                padding="16px"
                hasArrow
                borderRadius="4px"
              >
                {children}
              </Tooltip>
            )}
          >
            <Text color={colors.black[70]}>{getValue()}</Text>
          </TooltipWrapper>
        );
      },
      meta: {
        style: { padding: '16px' },
      },
      size: 400,
    },
    ...reportingDateColumns,
  ];

  const table = useReactTable({
    data: eligibilityData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} frozenColumnIds={['eligibilityTestName']} />;
};

type EligibilityTestsProps = {
  historicalRecords: any[];
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  creditFacilityId: string;
};
export const EligibilityTests = ({
  historicalRecords,
  loanProductMechanic,
  creditFacilityId,
}: EligibilityTestsProps) => {
  const toast = useToast();

  const [downloadFileName, setDownloadFileName] = useState<string>();

  // determines which column header should have a loading spinner
  // rest of column download buttons should be disabled
  // only allow single download at a time
  const [
    loanTapeDownloadReportingDate,
    setLoanTapeDownloadReportingDate,
  ] = useState<string>();

  const [
    getExtendedResponseTapeDownloadUrl,
  ] = useGetExtendedResponseTapeDownloadUrlLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (
        response &&
        response.getExtendedResponseTapeDownloadUrl &&
        downloadFileName
      ) {
        const { getExtendedResponseTapeDownloadUrl: url } = response;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, downloadFileName);
            successToastConfig.title = `Successfully downloaded ${downloadFileName}`;
            toast(successToastConfig);
            setLoanTapeDownloadReportingDate(undefined);
            setDownloadFileName(undefined);
          })
          .catch(() => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${downloadFileName}`;
              toast(errorToastConfig);
            }
            setLoanTapeDownloadReportingDate(undefined);
            setDownloadFileName(undefined);
          });
      }
    },
    onError: () => {
      setLoanTapeDownloadReportingDate(undefined);
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error downloading ${downloadFileName}`;
        toast(errorToastConfig);
      }
    },
  });

  const downloadFile = (
    reportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => {
    setDownloadFileName(
      `${
        loanProductMechanic.entity.slug
      }-extended-response-tape-${reportingDate}${
        fileVersion > 1 ? `-v${fileVersion}` : ''
      }.csv`,
    );
    getExtendedResponseTapeDownloadUrl({
      variables: {
        creditFacilityId,
        loanProductId: loanProductMechanic.entity.id,
        reportingDate,
        fileVersion,
      },
    });
  };

  return (
    <>
      <Box
        backgroundColor={colors.background[50]}
        mt="40px"
        border="none"
        borderRadius="8px"
      >
        <Flex padding="40px">
          <Text fontSize="2xl" fontWeight="semibold">
            Eligibility Tests
          </Text>
        </Flex>
        <Box padding="0px 40px 40px">
          <EligibilityTestsTable
            historicalRecords={historicalRecords}
            loanProductMechanic={loanProductMechanic}
            downloadFile={downloadFile}
            loanTapeDownloadReportingDate={loanTapeDownloadReportingDate}
            setLoanTapeDownloadReportingDate={setLoanTapeDownloadReportingDate}
          />
        </Box>
      </Box>
    </>
  );
};
